import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChatFeedbackFilters } from "@/components/chat-feedbacks/chat-feedback-filters";
import { ChatFeedbackTable } from "@/components/chat-feedbacks/chat-feedback-table";
import { FeedbackDetailDrawer } from "@/components/chat-feedbacks/feedback-detail-drawer";
import { chatFeedbacksQueryOptions } from "@/hooks/use-chat-feedbacks";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type {
  ChatFeedbackItem,
  ChatFeedbacksFilters,
} from "@/types/chat-feedback";

export default function ChatFeedbacksPage() {
  const [filters, setFiltersState] = useState<ChatFeedbacksFilters>({});
  const pagination = usePagination({ initialLimit: 20 });
  const queryClient = useQueryClient();

  const [selectedFeedback, setSelectedFeedback] =
    useState<ChatFeedbackItem | null>(null);
  const drawerOpen = selectedFeedback !== null;

  useEffect(() => {
    queryClient.prefetchQuery(
      chatFeedbacksQueryOptions(filters, {
        page: pagination.page + 1,
        limit: pagination.limit,
      }),
    );
  }, [queryClient, filters, pagination.page, pagination.limit]);

  const setFilters = useCallback(
    (next: ChatFeedbacksFilters) => {
      setFiltersState(next);
      pagination.setPage(1);
    },
    [pagination],
  );

  const handleOpenSession = useCallback((item: ChatFeedbackItem) => {
    setSelectedFeedback(item);
  }, []);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedFeedback(null);
    }
  }, []);

  const queryOptions = useMemo(
    () =>
      chatFeedbacksQueryOptions(filters, {
        page: pagination.page,
        limit: pagination.limit,
      }),
    [filters, pagination.page, pagination.limit],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          AI 챗 피드백
        </h1>
        <p className="text-muted-foreground leading-normal">
          사용자 피드백(추천/비추)이 달린 AI 챗 응답을 조회합니다.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>피드백 목록</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <ChatFeedbackFilters
            filters={filters}
            onFiltersChange={setFilters}
          />

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback />}>
                  <SuspenseQuery {...queryOptions}>
                    {({ data }) => (
                      <ChatFeedbackTable
                        data={data.items}
                        meta={data.meta}
                        pagination={pagination}
                        onOpenSession={handleOpenSession}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>

      <FeedbackDetailDrawer
        feedback={selectedFeedback}
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
      />
    </div>
  );
}
